import { Recurrence } from '@tableau/self-service-schedules';
import { SelfServiceSchedulePane, SelfServiceSchedulePaneType } from '@tableau/self-service-schedules/lib/src/components/SelfServiceSchedulePane';
import { DropdownButtonType, IntegerStepperWidget, List, ListItem, SingleSelectDropdown, TabStyles, TextFieldWidget } from '@tableau/widgets';
import * as React from 'react';
import { style } from 'typestyle';

import { LinkedTaskScheduleCheckbox } from '~src/linkedTasks/components/LinkedTaskScheduleCheckbox';

import { Messages } from '../../codegen/Localize';
import { ReactActionDialogBodyProps } from '../../commonInfra/dialogs/ReactActionDialog';
import { FeatureFlags } from '../../ts/FeatureFlags';
import { ExecutionType, ScheduleAction } from '../types/ISchedule';
import { getDefaultTimeZoneDetails, getPriorityRange, localizeExecutionType, localizeScheduleAction } from '../utils/ScheduleUtils';

export interface ScheduleCreateOrEditActionBodyProps {
  dialogMode: SelfServiceSchedulePaneType;
  isNew: boolean;
  isNameEditingDisabled?: boolean;
}

export interface StateForScheduleCreateOrEditActionBody {
  id?: string;
  name: string;
  scheduledAction: ScheduleAction;
  priority: number;
  parallel: boolean;
  recurrence: Recurrence;
  enabledForLinkedTasks?: boolean;
}
export type PropsForScheduleCreateOrEditActionBody = ReactActionDialogBodyProps<ScheduleCreateOrEditActionBodyProps, StateForScheduleCreateOrEditActionBody>;

function getScheduleActionTypes(): ListItem[] {
  let result = Object.keys(ScheduleAction).map((action: ScheduleAction) => ({
    value: action,
    content: localizeScheduleAction(action),
  }));
  const enableDataAcceleration = FeatureFlags.workbookAccelerationUIEnabled && FeatureFlags.adminSelfServiceSchedulesEnabled;
  if (!enableDataAcceleration) {
    result = result.filter(item => item.value !== ScheduleAction.Data_Acceleration);
  }
  return result;
}

function getParallelizationOptions(): ListItem[] {
  return Object.keys(ExecutionType).map((type: ExecutionType) => ({
    value: type,
    content: localizeExecutionType(type),
  }));
}


export function ScheduleCreateOrEditActionBody(props: PropsForScheduleCreateOrEditActionBody) {
  const parallelOptionsList = <List items={getParallelizationOptions()} style={{ maxHeight: '200px', overflow: 'auto' }} />;
  const parallelSelectedItem = props.dialogState.parallel
    ? Messages.adminSelfServiceSchedulesDialogExecutionParallel()
    : Messages.adminSelfServiceSchedulesDialogExecutionSerial();
  const fieldStyle = {
    paddingBottom: 12,
    flex: 1,
    minWidth: 225,
    maxWidth: 225,
  };

  const actionTypeOptionsList = <List items={getScheduleActionTypes()} style={{ maxHeight: '200px', overflow: 'auto' }} />;
  const selectedActionType = getScheduleActionTypes().find(t => t.value === props.dialogState.scheduledAction);
  const priorityRange = getPriorityRange(props.dialogState.scheduledAction);
  const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val));

  return <>
    <div className={style({ display: 'flex', flexDirection: 'row', flexWrap: 'wrap' })}>
      <TextFieldWidget
        label={Messages.adminSelfServiceSchedulesDialogName()}
        onChange={e => props.updateDialogState({ name: e.target.value })}
        onClear={() => props.updateDialogState({ name: '' })}
        placeholder={Messages.adminSelfServiceSchedulesDialogNamePlaceholder()}
        testId="schedule-name"
        value={props.dialogState.name}
        kind="line"
        className={style(fieldStyle)}
        disabled={props.isNameEditingDisabled}
      />
      <SingleSelectDropdown
        label={Messages.adminSelfServiceSchedulesDialogType()}
        list={actionTypeOptionsList}
        onChange={scheduledAction => props.updateDialogState({ scheduledAction: scheduledAction as ScheduleAction })}
        type={DropdownButtonType.Line}
        value={props.dialogState.scheduledAction}
        testId="schedule-type"
        className={style(fieldStyle)}
        disabled={!props.isNew}
      >
        {selectedActionType.content}
      </SingleSelectDropdown>
      <div
        className={style({
          ...fieldStyle,
        })}
      >
        <IntegerStepperWidget
          max={priorityRange.max}
          min={priorityRange.min}
          start={clamp(props.dialogState.priority, priorityRange.min, priorityRange.max)}
          testId="schedule-priority"
          onValueUpdated={(priority: number) => props.updateDialogState({ priority })}
          label={Messages.adminSelfServiceSchedulesDialogPriority()}
        />
      </div>
      <SingleSelectDropdown
        label={Messages.adminSelfServiceSchedulesDialogExecution()}
        list={parallelOptionsList}
        onChange={(execution: string) => props.updateDialogState({
          parallel: execution === ExecutionType.Parallel
        })}
        type={DropdownButtonType.Line}
        value={props.dialogState.parallel ? ExecutionType.Parallel : ExecutionType.Serial}
        testId="schedule-execution"
        className={style(fieldStyle)}
      >
        {parallelSelectedItem}
      </SingleSelectDropdown>
    </div>
    {props.dialogState.scheduledAction === ScheduleAction.Flow ?
      <LinkedTaskScheduleCheckbox
        checked={props.dialogState.enabledForLinkedTasks}
        checkboxEnabled={props.dialogState.parallel}
        handleChange={() => props.updateDialogState({ enabledForLinkedTasks: !props.dialogState.enabledForLinkedTasks })}
      /> : null}
    <label
      id="schedule-dialog-sss-pane-label"
      htmlFor="schedule-dialog-sss-pane"
      style={{
        color: TabStyles.Typography.Color.Secondary,
        paddingBottom: 6
      }}
    >{Messages.adminSelfServiceSchedulesDialogFrequency()}</label>
    <div id="schedule-dialog-sss-pane">
      <SelfServiceSchedulePane
        recurrenceModel={props.dialogState.recurrence}
        recurrenceModelChangedCallback={recurrence => props.updateDialogState({ recurrence })}
        timeZoneDetails={getDefaultTimeZoneDetails()}
        type={props.dialogMode}
      />
    </div>
  </>;
}
